import { useCallback, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { withFocused } from './virtualRange';

/** A list longer than this mounts only the rows in view (plus a few either side). */
export const VIRTUAL_MIN = 50;

const OVERSCAN = 8;
const SPACING = {
  1: { gap: 'gap-1', pad: 'pb-1' },
  2: { gap: 'gap-2', pad: 'pb-2' },
} as const;

type Props<T> = {
  items: readonly T[];
  getKey: (item: T) => string;
  /** The height of a row in px, spacing included. A row that grows (an opened evidence panel) is measured. */
  rowHeight: number;
  /** The space between rows, in Tailwind spacing units. */
  spacing: keyof typeof SPACING;
  /** A short list also scrolls in a box of the windowed list's height, instead of running full length. */
  boxed?: boolean;
  /** The classes of each row's `<li>`. */
  rowClassName?: string;
  /** The content of a row; the list supplies its `<li>`. */
  renderRow: (item: T) => ReactNode;
};

/**
 * A sidebar list. Up to VIRTUAL_MIN rows render as a plain list; a longer one scrolls in its own box and
 * mounts only the rows in view, so a group of thousands stays cheap. Rows stay in list order in the DOM, so
 * Tab walks them top to bottom; scrolling a focused row into view mounts the next ones. The row that has
 * keyboard focus stays mounted however far the list scrolls, so focus is never dropped.
 */
export function VirtualList<T>({
  items,
  getKey,
  rowHeight,
  spacing,
  boxed = false,
  rowClassName,
  renderRow,
}: Props<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const windowed = items.length > VIRTUAL_MIN;
  // The index of the row that holds focus; `null` once focus leaves the list.
  const [focused, setFocused] = useState<number | null>(null);
  const keptFocused = focused !== null && focused < items.length ? focused : null;
  const rangeExtractor = useCallback(
    (range: Range) => withFocused(defaultRangeExtractor(range), keptFocused),
    [keptFocused],
  );
  // The app does not run the React Compiler, so nothing memoises this component; the warning does not apply.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: windowed ? items.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN,
    getItemKey: (index) => getKey(items[index]),
    rangeExtractor,
  });
  const trackFocus = (event: FocusEvent<HTMLDivElement>) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('li[data-index]');
    if (row) setFocused(Number(row.dataset.index));
  };
  const dropFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(null);
  };
  const { gap, pad } = SPACING[spacing];

  if (!windowed) {
    return (
      <ul className={clsx('flex flex-col', gap, boxed && 'max-h-96 overflow-y-auto')}>
        {items.map((item) => (
          <li key={getKey(item)} className={rowClassName}>
            {renderRow(item)}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div ref={scrollRef} className="max-h-96 overflow-y-auto" onFocus={trackFocus} onBlur={dropFocus}>
      {/* Height and offsets come from the virtualizer, so they are inline styles. */}
      <ul className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((row) => (
          <li
            key={row.key}
            data-index={row.index}
            // Only some rows are in the DOM; tell assistive tech the list's real length and each row's place.
            aria-setsize={items.length}
            aria-posinset={row.index + 1}
            ref={virtualizer.measureElement}
            className={clsx('absolute top-0 left-0 w-full', pad, rowClassName)}
            style={{ transform: `translateY(${row.start}px)` }}
          >
            {renderRow(items[row.index])}
          </li>
        ))}
      </ul>
    </div>
  );
}
